/**
 * Notices the studio raises without a deploy (D-790).
 *
 * Separate from what's-new, and the difference is the whole point: an entry
 * there ships inside the build, so announcing a live defect that way would need
 * the very deploy you are trying to avoid. A notice is data.
 */
export type NoticeTone = 'Info' | 'Issue'

export type Notice = {
  id: string
  headline: string
  body: string
  tone: NoticeTone
  dismissible: boolean
}

/** Live, for me, and not already cleared. The server decides all three. */
export async function fetchNotices(): Promise<Notice[]> {
  const res = await fetch('/api/notices')

  /*
   * <b>An empty list rather than a throw</b> (D-209, one step on). A banner is
   * furniture on every screen in the app, so a failure here must not take a
   * page down with it — and "we could not check for notices" is not a sentence
   * worth interrupting anybody for. The cost is a notice going unseen for one
   * load, which is the lesser of the two.
   */
  if (!res.ok) return []

  return res.json()
}

export async function dismissNotice(id: string): Promise<void> {
  await fetch(`/api/notices/${id}/dismiss`, { method: 'POST' })
}

/** A notice as the studio sees it, live or retired (D-790). */
export type AdminNotice = {
  id: string
  headline: string
  body: string
  for: string[]
  tone: NoticeTone
  dismissible: boolean
  live: boolean
  publishedAt: string | null
  retiredAt: string | null
}

export type NoticeDraft = {
  headline: string
  body: string
  for: string[]
  tone: NoticeTone
  dismissible: boolean
}

async function orThrow(res: Response, what: string) {
  if (res.ok) return res
  const problem = await res.json().catch(() => null)
  throw new Error(problem?.detail ?? problem?.title ?? `${what} (${res.status})`)
}

export async function fetchAllNotices(): Promise<AdminNotice[]> {
  const res = await fetch('/api/admin/notices')
  await orThrow(res, 'Could not load the notices')
  return res.json()
}

export async function writeNotice(draft: NoticeDraft): Promise<AdminNotice> {
  const res = await fetch('/api/admin/notices', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(draft),
  })

  await orThrow(res, 'Could not save that notice')
  return res.json()
}

export async function editNotice(id: string, draft: NoticeDraft): Promise<AdminNotice> {
  const res = await fetch(`/api/admin/notices/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(draft),
  })

  await orThrow(res, 'Could not save that notice')
  return res.json()
}

export async function publishNotice(id: string): Promise<void> {
  await orThrow(await fetch(`/api/admin/notices/${id}/publish`, { method: 'POST' }), 'Could not publish it')
}

export async function retireNotice(id: string): Promise<void> {
  await orThrow(await fetch(`/api/admin/notices/${id}/retire`, { method: 'POST' }), 'Could not retire it')
}
